import type { CameraShot, ChapterPreset, Vec3Spec } from '../engine/types';

const v = (x: number, y: number, z: number): Vec3Spec => ({ x, y, z });

function shot(
  id: string,
  start: number,
  end: number,
  rig: CameraShot['rig'],
  lens: number,
  from: Vec3Spec,
  to: Vec3Spec,
  targetFrom: Vec3Spec,
  targetTo: Vec3Spec = targetFrom,
  exposure = 1,
): CameraShot {
  return { id, start, end, rig, lens, from, to, targetFrom, targetTo, exposure, ease: 'smoother' };
}

export const CHAPTER_PRESETS: readonly ChapterPreset[] = [
  {
    id: 'veil',
    title: 'The Veil',
    duration: 45,
    palette: { sky: 0x07090e, fog: 0x17151a, ground: 0x4b4237, stone: 0x75695a, accent: 0xd9aa62 },
    fogDensity: 0.026,
    light: { color: 0xe5bd83, intensity: 2.6, position: v(-8, 12, 7), target: v(0, 1, -7), ambientColor: 0x6d7994, ambientIntensity: 0.45 },
    particles: [
      { kind: 'dust', count: 850, color: 0xc8ae83, bounds: v(18, 9, 25), opacity: 0.38, size: 0.035, drift: 0.19 },
      { kind: 'stars', count: 260, color: 0xd9d3bf, bounds: v(28, 18, 36), opacity: 0.32, size: 0.045, drift: 0.015 },
    ],
    characters: [
      { id: 'mary-narrator', role: 'mary', position: v(-1.3, 0, -1.7), rotationY: 0.3, pose: 'still' },
      { id: 'jesus-memory', role: 'jesus', position: v(0.8, 0, -7), rotationY: -0.2, scale: 0.92, pose: 'blessing' },
      { id: 'witness-memory', role: 'witness', position: v(2.3, 0, -7.8), rotationY: -0.5, scale: 0.86, pose: 'reaching' },
    ],
    shots: [
      shot('dust-void', 0, 10, 'close', 70, v(0, 0.4, 1.8), v(0.15, 1.4, 0.8), v(0, 1.1, -2), v(0, 1.2, -4), 0.62),
      shot('memory-fragments', 10, 25, 'dolly', 48, v(-3.2, 2.2, 5.2), v(2.8, 1.8, 3.8), v(0, 1.3, -5), v(0.6, 1.4, -7), 0.92),
      shot('passover-lamps', 25, 38, 'crane', 34, v(8.5, 6.4, 11.5), v(-5.2, 9.6, 15), v(0, 2.25, -8), v(0, 2.8, -12), 1.16),
      shot('mary-profile', 38, 45, 'close', 80, v(-3.1, 1.85, 1), v(-2.5, 1.9, 0.2), v(-1.3, 1.85, -1.7), undefined, 1.12),
    ],
  },
  {
    id: 'bread-and-night',
    title: 'Bread and Night',
    duration: 55,
    palette: { sky: 0x09090c, fog: 0x1c1717, ground: 0x40362c, stone: 0x6d6254, accent: 0xe1a553 },
    fogDensity: 0.021,
    light: { color: 0xffbd68, intensity: 3.4, position: v(-2, 7, 2), target: v(0, 1, -0.5), ambientColor: 0x4d5268, ambientIntensity: 0.38 },
    particles: [{ kind: 'dust', count: 480, color: 0xb99a72, bounds: v(9, 5, 8), opacity: 0.23, size: 0.025, drift: 0.07 }],
    characters: [
      { id: 'jesus', role: 'jesus', position: v(0, 0, -1.25), rotationY: 0, pose: 'seated' },
      { id: 'mary', role: 'mary', position: v(-3.15, 0, -0.8), rotationY: 1.15, pose: 'seated' },
      { id: 'peter', role: 'peter', position: v(2.7, 0, -0.65), rotationY: -1.2, pose: 'seated' },
      { id: 'judas', role: 'judas', position: v(1.4, 0, 0.4), rotationY: 1.45, pose: 'seated' },
      { id: 'disciple-1', role: 'disciple', position: v(-1.65, 0, 0.45), rotationY: 2.8, pose: 'seated' },
      { id: 'disciple-2', role: 'disciple', position: v(3.8, 0, 0.35), rotationY: -2.4, pose: 'seated' },
    ],
    shots: [
      shot('room-tableau', 0, 11, 'tableau', 42, v(0, 3.35, 10.2), v(-0.8, 3.0, 7.7), v(0, 1.35, -0.5), v(0, 1.45, -0.8), 1.48),
      shot('service', 11, 21, 'orbit', 46, v(3.4, 1.7, 3.2), v(-2.6, 1.6, 2.8), v(0, 0.38, 0.82), v(-0.35, 0.46, 0.72), 1.32),
      shot('bread-hands', 21, 38, 'close', 52, v(-1.8, 2.02, 4.4), v(-1.0, 1.94, 3.7), v(0, 1.43, -1.15), undefined, 1.42),
      shot('judas-departure', 38, 45, 'dolly', 56, v(2.85, 2.15, 3.45), v(3.25, 2.02, 2.75), v(2.45, 1.48, -0.15), v(4.02, 1.4, -1.46), 1.02),
      shot('judas-threshold', 45, 51, 'close', 70, v(5.45, 2.35, 2.2), v(5.25, 2.3, 1.9), v(4.02, 1.7, -0.74), undefined, 1.02),
      shot('wine-to-moon', 51, 55, 'crane', 55, v(0.2, 1.15, 0.6), v(0, 7.5, -2), v(0, 0.9, -0.2), v(0, 3.5, -6), 0.74),
    ],
  },
  {
    id: 'garden-and-judgment',
    title: 'The Garden and Judgment',
    duration: 55,
    palette: { sky: 0x080b14, fog: 0x171c29, ground: 0x31382f, stone: 0x6e6a62, accent: 0xc98b49 },
    fogDensity: 0.018,
    light: { color: 0x9fb7d5, intensity: 2.7, position: v(-9, 13, -2), target: v(0, 1, -3), ambientColor: 0x38415d, ambientIntensity: 0.55 },
    particles: [
      { kind: 'mist', count: 720, color: 0xa5afba, bounds: v(15, 3, 18), opacity: 0.18, size: 0.045, drift: 0.12 },
      { kind: 'embers', count: 90, color: 0xf1a154, bounds: v(6, 5, 8), opacity: 0.36, size: 0.022, drift: 0.32 },
    ],
    characters: [
      { id: 'jesus', role: 'jesus', position: v(0, 0, -1.7), rotationY: 0, pose: 'kneeling' },
      { id: 'peter', role: 'peter', position: v(-4.4, 0, 1.3), rotationY: 0.5, pose: 'still' },
      { id: 'judas', role: 'judas', position: v(3.1, 0, -0.4), rotationY: -1.3, pose: 'walking' },
      { id: 'pilate', role: 'pilate', position: v(0, 0.86, -4.5), rotationY: 0, pose: 'seated' },
      { id: 'soldier-1', role: 'soldier', position: v(2.2, 0, -2), rotationY: -0.7, pose: 'still' },
      { id: 'soldier-2', role: 'soldier', position: v(-2.2, 0, -2.2), rotationY: 0.7, pose: 'still' },
      { id: 'disciple-1', role: 'disciple', position: v(-4.8, 0, -3.25), rotationY: 0.35, pose: 'mourning' },
      { id: 'disciple-2', role: 'disciple', position: v(4.45, 0, -3.7), rotationY: -0.45, pose: 'mourning' },
    ],
    shots: [
      shot('agony-wide', 0, 11, 'tableau', 44, v(6.2, 3.35, 7.5), v(4.4, 2.55, 5.5), v(0.2, 0.72, -2.4), v(0.8, 0.68, -1.9), 1.4),
      shot('agony-close', 11, 21, 'handheld', 62, v(3.9, 2.15, 4.25), v(3.45, 1.95, 3.6), v(1.15, 0.62, -1.45), undefined, 1.42),
      shot('arrest-torches', 21, 31, 'pov', 34, v(-2.5, 1.75, 3.8), v(1.8, 1.8, 2.8), v(0.5, 1.3, -1.4), undefined, 1.08),
      shot('denial-fire', 31, 42, 'dolly', 56, v(-6.2, 2.25, 6), v(-5.2, 2.05, 4.8), v(-4.2, 1.35, 1.3), v(-1.7, 1.35, -0.7), 1.02),
      shot('roman-authority-wide', 42, 48.5, 'dolly', 58, v(-3.1, 2.55, 4.2), v(-2.45, 2.42, 3.25), v(-0.35, 1.62, -3.05), v(0, 2.1, -4.45), 1.3),
      shot('roman-judgment', 48.5, 55, 'dolly', 62, v(3.05, 2.48, 4.1), v(2.25, 2.3, 3.45), v(0.25, 1.7, -3.2), v(0, 2.12, -4.42), 1.28),
    ],
  },
  {
    id: 'golgotha',
    title: 'Golgotha',
    duration: 115,
    palette: { sky: 0x82837d, fog: 0x8c887e, ground: 0x625747, stone: 0x817567, accent: 0xa54335 },
    fogDensity: 0.013,
    light: { color: 0xe1d8c4, intensity: 4.2, position: v(-11, 16, 8), target: v(0, 2, -4), ambientColor: 0x777b7d, ambientIntensity: 0.72 },
    particles: [
      { kind: 'dust', count: 1200, color: 0xb7a98f, bounds: v(22, 11, 30), opacity: 0.43, size: 0.042, drift: 0.5 },
      { kind: 'mist', count: 420, color: 0x47464c, bounds: v(16, 8, 16), opacity: 0.17, size: 0.075, drift: 0.14 },
    ],
    characters: [
      { id: 'jesus', role: 'jesus', position: v(0, 0, -3.05), rotationY: 0, pose: 'walking' },
      { id: 'mary', role: 'mary', position: v(-2.8, 0, -0.7), rotationY: 0.1, pose: 'mourning' },
      { id: 'witness-1', role: 'witness', position: v(2.8, 0, -0.2), rotationY: -0.5, pose: 'mourning' },
      { id: 'soldier-1', role: 'soldier', position: v(-3.2, 0, -2.6), rotationY: 0.6, pose: 'still' },
      { id: 'soldier-2', role: 'soldier', position: v(3.6, 0, -2.2), rotationY: -0.7, pose: 'still' },
      { id: 'simon', role: 'witness', position: v(-4.6, 0, -2.4), rotationY: 0.85, pose: 'walking', palette: [0x715845, 0x4a4037, 0x2a211b] },
    ],
    shots: [
      shot('road-low', 0, 13, 'handheld', 36, v(-4.6, 1.18, 6.9), v(-1.45, 1.08, 5.1), v(0, 1.5, -2.15), v(0, 1.35, -0.35), 1.7),
      shot('road-simon', 13, 26, 'pov', 48, v(4.25, 1.7, 5.6), v(-3.45, 1.48, 4.25), v(0, 1.42, -0.35), v(-0.45, 1.34, 0.82), 1.7),
      shot('raising-witness', 26, 40, 'pov', 42, v(-4.9, 1.52, 4.35), v(-3.75, 1.48, 3.05), v(-1.2, 2.25, -2.15), v(0, 3.18, -3.15), 1.08),
      shot('suspension-oblique', 40, 54, 'dolly', 55, v(4.85, 2.45, 3.35), v(3.65, 2.18, 2.25), v(0, 3.08, -3.12), v(0, 3.45, -3.12), 1.12),
      shot('mary-grief', 54, 68, 'close', 68, v(1.85, 2.46, 4.25), v(1.15, 2.3, 3.55), v(-2.35, 1.82, 0.82), v(-2.28, 1.76, 0.88), 1.18),
      shot('powers', 68, 91, 'orbit', 46, v(-6.4, 3.6, 3.4), v(5.8, 3.8, 2.8), v(0, 2.95, -3.08), undefined, 0.9),
      shot('last-breath', 91, 106, 'close', 68, v(2.55, 4.18, 1.85), v(1.72, 4.02, 1.2), v(0, 3.9, -3.08), v(0, 3.74, -3.08), 0.96),
      shot('ordinary-wind', 106, 115, 'dolly', 55, v(-4.45, 2.12, 5.25), v(-4.72, 2.06, 5.48), v(-0.15, 2.92, -3.05), undefined, 1.25),
    ],
  },
  {
    id: 'silence',
    title: 'Silence',
    duration: 60,
    palette: { sky: 0x111318, fog: 0x22232a, ground: 0x3e3b36, stone: 0x65635f, accent: 0x7b7180 },
    fogDensity: 0.026,
    light: { color: 0xd0cbd2, intensity: 5.2, position: v(-10, 10, 7), target: v(0, 1, 0), ambientColor: 0x858a9b, ambientIntensity: 1.55 },
    particles: [{ kind: 'mist', count: 760, color: 0x868691, bounds: v(14, 5, 16), opacity: 0.15, size: 0.055, drift: 0.045 }],
    characters: [
      { id: 'mary', role: 'mary', position: v(-2.1, 0, -0.3), rotationY: 0.1, pose: 'mourning' },
      { id: 'peter', role: 'peter', position: v(2.7, 0, 0.4), rotationY: -0.5, pose: 'mourning' },
      { id: 'mother', role: 'mary', position: v(-4.5, 0, -1), rotationY: 0.5, pose: 'mourning' },
    ],
    shots: [
      shot('linen-labor', 0, 13, 'crane', 48, v(1.15, 6.4, 4.65), v(-0.55, 6.1, 4.15), v(0.08, 0.48, -1.86), v(-0.08, 0.48, -1.92), 1.18),
      shot('stone-weight', 13, 24, 'dolly', 34, v(-6.2, 3.0, 9.2), v(5.6, 2.8, 8.2), v(0, 1.15, -2.55), undefined, 0.82),
      shot('sabbath-mary', 24, 29, 'close', 82, v(-2.75, 1.92, 2.1), v(-2.6, 1.88, 1.82), v(-1.65, 1.58, -1.2), undefined, 0.7),
      shot('sabbath-peter', 29, 34, 'close', 82, v(2.8, 1.94, 2.2), v(2.62, 1.88, 1.9), v(1.65, 1.58, -1.35), undefined, 0.68),
      shot('sabbath-mother-city', 34, 40, 'close', 82, v(-0.95, 2.08, 2.35), v(-0.82, 2.0, 1.98), v(-0.25, 1.66, -3.7), undefined, 0.82),
      shot('memory-shame', 40, 52, 'orbit', 58, v(3.3, 2.25, 5.2), v(-3.1, 2.1, 4.8), v(0, 1.55, -2.1), undefined, 0.78),
      shot('first-light', 52, 60, 'close', 62, v(-3.35, 2.05, 4.2), v(-3, 1.95, 3.6), v(-1.3, 1.42, -1.1), undefined, 0.82),
    ],
  },
  {
    id: 'garden-reopened',
    title: 'The Garden Reopened',
    duration: 75,
    palette: { sky: 0x6c8194, fog: 0xa69e8c, ground: 0x5b6450, stone: 0x8b8374, accent: 0xf0c47a },
    fogDensity: 0.017,
    light: { color: 0xffdfad, intensity: 4.5, position: v(-10, 10, 6), target: v(0, 1, 0), ambientColor: 0xb6c7d3, ambientIntensity: 1.25 },
    particles: [
      { kind: 'mist', count: 620, color: 0xdbd8ca, bounds: v(13, 3, 16), opacity: 0.2, size: 0.05, drift: 0.08 },
      { kind: 'pollen', count: 520, color: 0xf2d38f, bounds: v(11, 7, 14), opacity: 0.45, size: 0.032, drift: 0.2 },
    ],
    characters: [
      { id: 'mary', role: 'mary', position: v(-1.6, 0, -0.1), rotationY: 0.2, pose: 'mourning' },
      { id: 'jesus', role: 'jesus', position: v(1.5, 0, 0.15), rotationY: -0.5, pose: 'still' },
      { id: 'witness-1', role: 'witness', position: v(-4.2, 0, 1.2), rotationY: 0.6, pose: 'reaching' },
      { id: 'witness-2', role: 'witness', position: v(-5.1, 0, 0.3), rotationY: 0.8, pose: 'still' },
    ],
    shots: [
      shot('dawn-approach', 0, 14, 'dolly', 52, v(-3.8, 2.2, 6.5), v(-2.6, 1.95, 4.6), v(-0.75, 1.05, -2.25), undefined, 0.82),
      shot('open-stone', 14, 26, 'close', 58, v(3.3, 1.82, 4.45), v(2.85, 1.7, 3.65), v(0, 0.62, -3.0), undefined, 0.94),
      shot('mistaken-gardener', 26, 34, 'dolly', 58, v(0.15, 2.0, 4.8), v(0.45, 1.86, 4.05), v(0.45, 1.36, -0.72), undefined, 0.9),
      shot('mary-turns', 34, 42, 'tableau', 60, v(0.2, 1.92, 3.9), v(0.2, 1.88, 3.65), v(0.2, 1.42, -0.42), undefined, 1.0),
      shot('mary-name', 42, 55, 'close', 60, v(0.08, 2.06, 4.55), v(0.12, 1.98, 3.95), v(0.12, 1.58, -0.2), undefined, 1.06),
      shot('retained-wounds', 55, 65, 'close', 68, v(3.4, 2.05, 3.45), v(2.95, 1.94, 2.85), v(1.12, 1.48, -0.38), undefined, 1.02),
      shot('garden-wide', 65, 75, 'crane', 35, v(7, 3.2, 7.5), v(-7, 7.2, 10), v(0, 1.35, -2), v(0, 1.7, -4), 1.18),
    ],
  },
  {
    id: 'eyes-opened',
    title: 'Eyes Opened',
    duration: 85,
    palette: { sky: 0x8ba5b3, fog: 0xc0b9a5, ground: 0x76725e, stone: 0x938b79, accent: 0xf1c870 },
    fogDensity: 0.012,
    light: { color: 0xffe5b7, intensity: 4.8, position: v(-12, 15, 3), target: v(0, 1, -6), ambientColor: 0x92aabc, ambientIntensity: 0.9 },
    particles: [
      { kind: 'pollen', count: 780, color: 0xf3dc9b, bounds: v(18, 9, 22), opacity: 0.42, size: 0.035, drift: 0.18 },
      { kind: 'dust', count: 380, color: 0xd0c2a6, bounds: v(17, 8, 25), opacity: 0.2, size: 0.04, drift: 0.08 },
    ],
    characters: [
      { id: 'jesus', role: 'jesus', position: v(0, 0, -3), rotationY: 0, pose: 'blessing' },
      { id: 'mary', role: 'mary', position: v(-2.4, 0, -0.4), rotationY: 0.3, pose: 'reaching' },
      { id: 'peter', role: 'peter', position: v(2.5, 0, 0.2), rotationY: -0.5, pose: 'kneeling' },
      { id: 'disciple-1', role: 'disciple', position: v(-4.2, 0, -1.1), rotationY: 0.7, pose: 'still' },
      { id: 'disciple-2', role: 'disciple', position: v(4.4, 0, -1.3), rotationY: -0.8, pose: 'still' },
    ],
    shots: [
      shot('mary-testimony', 0, 9, 'close', 58, v(-2.6, 1.94, 4.2), v(-2.45, 1.9, 3.75), v(-1.0, 1.58, 0.08), undefined, 0.98),
      shot('peter-doubts', 9, 16, 'close', 76, v(2.85, 1.94, 3.05), v(2.6, 1.88, 2.7), v(1.05, 1.52, -0.18), undefined, 0.92),
      shot('testimony-eyeline', 16, 23, 'dolly', 58, v(-0.22, 1.98, 4.65), v(0.22, 1.94, 4.3), v(0, 1.56, -0.08), undefined, 1.0),
      shot('emmaus-bread', 23, 37, 'dolly', 78, v(2.45, 1.78, 4.15), v(-2.2, 1.74, 4.0), v(0, 1.18, -2.02), undefined, 1.08),
      shot('thomas-hesitates', 37, 45, 'close', 64, v(0.15, 1.95, 3.8), v(0.05, 1.88, 3.35), v(0.08, 1.42, -0.28), undefined, 1.06),
      shot('thomas-wounds', 45, 54, 'close', 90, v(0.08, 1.68, 2.55), v(0.04, 1.62, 2.28), v(0.05, 1.43, -0.18), undefined, 1.08),
      shot('peter-fire', 54, 66, 'dolly', 58, v(0.22, 1.58, 4.25), v(0.05, 1.46, 3.78), v(0, 0.98, -0.46), undefined, 1.08),
      shot('mountain-stillness', 66, 72, 'close', 68, v(2.25, 2.08, 4.25), v(1.95, 2.0, 3.85), v(0, 1.55, -1.25), undefined, 1.08),
      shot('ascension-faces', 72, 76, 'dolly', 70, v(-1.65, 1.96, 3.15), v(1.65, 1.92, 3.05), v(0, 1.52, -0.18), undefined, 1.12),
      shot('creation-gift', 76, 85, 'crane', 48, v(0, 6.2, 7.4), v(0, 3.2, 5.4), v(0, 0.55, -1.45), v(0, 0.72, -1.65), 1.16),
    ],
  },
] as const;

export const PRESET_BY_ID = Object.fromEntries(CHAPTER_PRESETS.map((preset) => [preset.id, preset])) as Record<ChapterPreset['id'], ChapterPreset>;

export const FILM_DURATION = CHAPTER_PRESETS.reduce((total, chapter) => total + chapter.duration, 0);

export const CHAPTER_STARTS = CHAPTER_PRESETS.reduce<Record<ChapterPreset['id'], number>>((starts, chapter) => {
  const previous = Object.values(starts);
  const elapsed = previous.length === 0
    ? 0
    : CHAPTER_PRESETS.slice(0, previous.length).reduce((sum, item) => sum + item.duration, 0);
  starts[chapter.id] = elapsed;
  return starts;
}, {} as Record<ChapterPreset['id'], number>);
